/**
 * CORE-1375 — есть ли у материала картинка.
 *
 * Записи комнат, продвинутые в библиотеку как практики, бывают двух видов без
 * картинки, и бэк ни один не помечает:
 *   • без видеодорожки вовсе — practice 313 на dev: `videoWidth × videoHeight
 *     = 0 × 0`, файл валиден, звук есть;
 *   • с видеодорожкой из сплошь чёрных кадров — practice 662 на dev: камеры в
 *     комнате были выключены, транскод честный h264 854×480, и каждый кадр
 *     чёрный. По метаданным от настоящего видео не отличить.
 *
 * Отдельный скрытый `<video crossorigin="anonymous" preload="metadata">` на
 * mp4-кандидате: метаданные дают размер кадра, три позиции по длине (10, 40 и
 * 70 %) — яркость. Зонд не трогает играющий элемент и его режим загрузки
 * (CORE-1361), обходится range-запросами и, если бакет не отдаёт CORS или
 * файл не грузится, честно отвечает «не знаю» — тогда сцена ведёт себя как
 * раньше. Вердикт запоминается по адресу: комната и страница практики не
 * гоняют зонд повторно.
 */
export type MaterialVideoVerdict = 'video' | 'audio-only' | 'blank' | 'unknown';

/** Порог «чёрного»: максимум по каналам у самого светлого пикселя, из 255.
 *  Чистый чёрный после YUV→RGB даёт 0–3; тёмная сцена настоящего видео —
 *  десятки. */
export const BLANK_LUMA_MAX = 10;
/** Доли длины, на которых берутся кадры: затемнённое вступление настоящего
 *  видео не дотягивается до всех трёх. */
export const PROBE_POSITIONS = [0.1, 0.4, 0.7] as const;
/** Сторона уменьшенного холста — яркость считается по превью, не по кадру. */
export const PROBE_CANVAS_SIZE = 32;
export const PROBE_TIMEOUT_MS = 15_000;

/** Кадр пуст, если ни один пиксель не светлее порога (RGBA-байты холста). */
export function isBlankFrame(rgba: ArrayLike<number>, threshold = BLANK_LUMA_MAX): boolean {
    for (let i = 0; i + 2 < rgba.length; i += 4) {
        if (rgba[i] >= threshold || rgba[i + 1] >= threshold || rgba[i + 2] >= threshold) return false;
    }
    return true;
}

/** Первый mp4 среди кандидатов: HLS-плейлист зондом не читается. */
export function pickProbeUrl(candidates: ReadonlyArray<string | null | undefined>): string | null {
    for (const u of candidates) {
        if (typeof u !== 'string' || !u) continue;
        const path = u.split(/[?#]/)[0].toLowerCase();
        if (path.endsWith('.mp4') || path.endsWith('.m4v') || path.endsWith('.mov')) return u;
    }
    return null;
}

/** Вердикт по размерам кадра и пустоте выбранных кадров. */
export function decideVerdict(args: {
    videoWidth: number;
    videoHeight: number;
    /** `null` — кадр взять не удалось (холст «запачкан» или ошибка). */
    blankFrames: ReadonlyArray<boolean | null>;
}): MaterialVideoVerdict {
    if (!(args.videoWidth > 0 && args.videoHeight > 0)) return 'audio-only';
    const known = args.blankFrames.filter((b): b is boolean => b !== null);
    if (known.length === 0) return 'unknown';
    if (known.some((b) => !b)) return 'video';
    // Все взятые кадры пусты — но одного кадра мало: это могло быть вступление.
    return known.length >= 2 ? 'blank' : 'unknown';
}

/** Есть ли у материала картинка по вердикту зонда. */
export function verdictHidesPicture(v: MaterialVideoVerdict | null | undefined): boolean {
    return v === 'audio-only' || v === 'blank';
}

const cache = new Map<string, Promise<MaterialVideoVerdict>>();

/** Только для тестов и повторного зонда после смены файла под тем же адресом. */
export function resetMaterialVideoProbeCache(): void {
    cache.clear();
}

export function probeMaterialVideo(url: string): Promise<MaterialVideoVerdict> {
    const cached = cache.get(url);
    if (cached) return cached;
    const run = runProbe(url).catch(() => 'unknown' as MaterialVideoVerdict);
    cache.set(url, run);
    // «Не знаю» не кэшируется: сеть могла просто моргнуть.
    run.then((v) => { if (v === 'unknown') cache.delete(url); });
    return run;
}

function runProbe(url: string): Promise<MaterialVideoVerdict> {
    if (typeof document === 'undefined') return Promise.resolve('unknown');
    return new Promise<MaterialVideoVerdict>((resolve) => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        const canvas = document.createElement('canvas');
        canvas.width = PROBE_CANVAS_SIZE;
        canvas.height = PROBE_CANVAS_SIZE;
        const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;

        let done = false;
        const finish = (v: MaterialVideoVerdict) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            try { video.pause(); video.removeAttribute('src'); video.load(); } catch { /* ignore */ }
            resolve(v);
        };
        const timer = setTimeout(() => finish('unknown'), PROBE_TIMEOUT_MS);

        const sampleFrame = (): boolean | null => {
            if (!ctx) return null;
            try {
                ctx.drawImage(video, 0, 0, PROBE_CANVAS_SIZE, PROBE_CANVAS_SIZE);
                const { data } = ctx.getImageData(0, 0, PROBE_CANVAS_SIZE, PROBE_CANVAS_SIZE);
                return isBlankFrame(data);
            } catch {
                // SecurityError: холст запачкан — CORS бакет не отдал. Не знаем.
                return null;
            }
        };

        const seekTo = (t: number) => new Promise<void>((res) => {
            const onSeeked = () => { video.removeEventListener('seeked', onSeeked); res(); };
            video.addEventListener('seeked', onSeeked);
            video.currentTime = t;
        });

        video.addEventListener('error', () => finish('unknown'));
        video.addEventListener('loadedmetadata', async () => {
            const { videoWidth, videoHeight, duration } = video;
            if (!(videoWidth > 0 && videoHeight > 0)) { finish('audio-only'); return; }
            if (!Number.isFinite(duration) || duration <= 0) { finish('unknown'); return; }
            const blankFrames: Array<boolean | null> = [];
            for (const p of PROBE_POSITIONS) {
                if (done) return;
                await seekTo(duration * p);
                const blank = sampleFrame();
                blankFrames.push(blank);
                // Первый же непустой кадр — это видео, дальше смотреть незачем.
                if (blank === false) break;
                // Холст запачкан — остальные кадры дадут то же.
                if (blank === null) break;
            }
            finish(decideVerdict({ videoWidth, videoHeight, blankFrames }));
        });
        video.src = url;
        video.load();
    });
}
